import api from "./api";

export class SSEClient<D = unknown> {
  private eventSource: EventSource | null = null;
  private token: string;
  private default_err_msg = "Unexpected Error";
  private url: string = "";
  private onMessage: ((data: D) => void) | null = null;
  private onError: ((error: Event, msg?: string) => void) | null = null;
  private onClose: (() => void) | null = null;
  private retryCount: number = 0;
  private maxRetries: number = 6;
  private retryDelay: number = 2000; // 2 seconds
  private retryTimeoutId: number | null = null;
  private shouldRetry: boolean = true;

  constructor(token: string, default_err_msg?: string) {
    this.token = token;
    if (default_err_msg) {
      this.default_err_msg = default_err_msg;
    }
  }

  connect(
    url: string,
    onMessage: (data: D) => void,
    onClose?: () => void,
    onError?: (error: Event, msg?: string) => void
  ): void {
    this.url = url;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onClose = onClose;
    this.createEventSource();
  }

  private createEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      console.groupEnd();
    }

    const baseURL = api.defaults.baseURL || window.location.origin;
    const fullUrl = `${baseURL}${this.url}`;
    const urlWithToken = `${fullUrl}?token=${encodeURIComponent(this.token)}`;
    this.eventSource = new EventSource(urlWithToken);

    // In test environment, disable retries to avoid timeouts
    // EventSourceMock sets readyState to CLOSED immediately, causing immediate failures
    if (typeof jest !== "undefined") {
      this.shouldRetry = false;
      this.maxRetries = 0;
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.eventSource) return;
    console.groupCollapsed(`🚀 SSE Event Stream ["${this.url}"]`);
    this.eventSource.onopen = (): void => {
      if (this.retryCount > 0) {
        console.info("✅ SSE connection reestablished", {
          retryCount: this.retryCount,
        });
      } else {
        console.info("✅ SSE connection established");
      }
      this.retryCount = 0;
      if (this.retryTimeoutId !== null) {
        clearTimeout(this.retryTimeoutId);
        this.retryTimeoutId = null;
      }
    };

    this.eventSource.addEventListener(
      "customerror",
      (event: MessageEvent): void => {
        const msg = `${event?.data}` || this.default_err_msg;
        console.error("❌ Custom error event received", {
          message: msg,
          event,
        });
        if (this.onError) {
          this.onError(event, msg);
        }
        this.close();
      }
    );

    this.eventSource.addEventListener("close", (event: MessageEvent): void => {
      console.groupCollapsed(
        `🔒 Close event received, closing connection [${
          event.data?.length || 0
        }]`
      );
      console.info("Length", event.data?.length || 0);
      try {
        const data = JSON.parse(event.data);
        console.info("Message:", data);
        if (this.onMessage) {
          this.onMessage(data);
        }
        // Close the connection
        this.close();
      } catch (error) {
        console.error("❌ Error parsing close event data", { error, event });
        this.close();
      }
      console.groupEnd();
    });

    this.eventSource.onmessage = (event): void => {
      try {
        const data = JSON.parse(event.data);
        if (this.onMessage) {
          this.onMessage(data);
        }
      } catch (error) {
        console.error("❌ Error parsing SSE message data", error);
      }
    };

    this.eventSource.onerror = (event): void => {
      const readyState = this.eventSource?.readyState;

      if (readyState === EventSource.CLOSED) {
        console.error("❌ SSE fatal error - connection closed", {
          readyState: "CLOSED",
          retryCount: this.retryCount,
          maxRetries: this.maxRetries,
          event,
        });

        if (this.shouldRetry && this.retryCount < this.maxRetries) {
          this.retryCount++;
          const delay = this.retryDelay * this.retryCount;
          console.info("🔁 Retrying SSE connection", {
            delay,
            attempt: this.retryCount,
            maxRetries: this.maxRetries,
          });

          this.retryTimeoutId = window.setTimeout(() => {
            this.createEventSource();
          }, delay);
        } else {
          console.error("❌ SSE connection failed after max retries", {
            retryCount: this.retryCount,
            maxRetries: this.maxRetries,
            shouldRetry: this.shouldRetry,
          });
          if (this.onError) {
            this.onError(
              event,
              "Connection failed after maximum retry attempts"
            );
          }
        }
      } else if (readyState === EventSource.CONNECTING) {
        console.info("🔄 SSE transient error - EventSource is reconnecting", {
          readyState: "CONNECTING",
        });
        this.retryCount = 0;
      }
    };
  }

  close(): void {
    this.shouldRetry = false;

    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.onClose) {
      this.onClose();
    }

    console.groupEnd();
  }
}
